import React from 'react';
import { Operation } from 'fast-json-patch';

import { ProfileIcon, Switch } from '@hcs/design-system';
import { MenuItem } from '@hcs/design-system';
import { UserGroupIcon } from '@hcs/design-system';
import {
  useFeatureFlagsForUser,
  usePatchReportPreferencesForOrg,
} from '@hcs/huell';
import { useReportPreferencesForOrg } from '@hcs/huell';
import { FeatureFlags, PublicLinkPermissions } from '@hcs/types';

import styles from './OrgReportPreferences.module.css';

const dataHcName = 'intra-org-report-access-control';
export const AllowPublicLinksControl = () => {
  const { isFetched, data: orgReportPrefs } = useReportPreferencesForOrg();
  const { data: featureFlags } = useFeatureFlagsForUser();
  const { mutate: patchOrgPrefs } = usePatchReportPreferencesForOrg();
  const deprecatedPreference: PublicLinkPermissions | undefined =
    orgReportPrefs?.preferences?.publicLinkPermissions;
  const readOnlyPreference: boolean | undefined =
    orgReportPrefs?.preferences?.publicLinksReadOnly;
  const editablePreference: boolean =
    orgReportPrefs?.preferences?.publicLinksEditable || false;
  return (
    <>
      <MenuItem
        dataHcName={`${dataHcName}-read-only`}
        Icon={UserGroupIcon}
        label={`Read-Only Public Links`}
        description={`Allow users to create read-only public sharing links. Turning this setting off will disable all previously created read-only public links.`}
        contentRight={
          isFetched &&
          patchOrgPrefs && (
            <div className={styles.Switch}>
              <Switch
                switchOffOption={{
                  value: false,
                  dataHcName: `${dataHcName}-read-only-off`,
                }}
                switchOnOption={{
                  value: true,
                  dataHcName: `${dataHcName}-read-only-on`,
                }}
                dataHcName={`${dataHcName}-read-only-switch`}
                value={
                  readOnlyPreference || deprecatedPreference === 'read-only'
                }
                onChange={(value) => {
                  const operations: Operation[] = [
                    {
                      op: 'add',
                      path: '/publicLinksReadOnly',
                      value,
                    },
                  ];
                  if (deprecatedPreference) {
                    operations.push({
                      op: 'remove',
                      path: '/publicLinkPermissions',
                    });
                  }
                  patchOrgPrefs(operations);
                }}
              />
              <div className={styles.SwitchLabel}>
                {readOnlyPreference ? 'Enabled' : 'Disabled'}
              </div>
            </div>
          )
        }
      />
      {featureFlags?.[FeatureFlags.PexpEditableLinks] && (
        <MenuItem
          dataHcName={`${dataHcName}-editable`}
          Icon={ProfileIcon}
          label={`Editable Public Links`}
          description={`Allow users to create editable public sharing links. Turning this setting off will disable all previously created editable public links.`}
          contentRight={
            isFetched &&
            patchOrgPrefs && (
              <div className={styles.Switch}>
                <Switch
                  switchOffOption={{
                    value: false,
                    dataHcName: `${dataHcName}-editable-off`,
                  }}
                  switchOnOption={{
                    value: true,
                    dataHcName: `${dataHcName}-editable-on`,
                  }}
                  dataHcName={`${dataHcName}-editable-switch`}
                  value={editablePreference}
                  onChange={(value) => {
                    const operations: Operation[] = [
                      {
                        op: 'add',
                        path: '/publicLinksEditable',
                        value,
                      },
                    ];
                    if (deprecatedPreference) {
                      operations.push({
                        op: 'remove',
                        path: '/publicLinkPermissions',
                      });
                    }
                    if (deprecatedPreference === 'read-only') {
                      operations.push({
                        op: 'add',
                        path: '/publicLinksReadOnly',
                        value: true,
                      });
                    }
                    patchOrgPrefs(operations);
                  }}
                />
                <div className={styles.SwitchLabel}>
                  {editablePreference ? 'Enabled' : 'Disabled'}
                </div>
              </div>
            )
          }
        />
      )}
    </>
  );
};
